export type AddOnAssignmentMode = 'company' | 'all_users' | 'specific_users';

export type AddOnApplicabilitySource = AddOnAssignmentMode | null;

export type AddOnKey =
  | 'webcam_capture'
  | 'custom_periodic_interval'
  | 'custom_screencast_limit';

export type SettingsScope = 'company' | 'employee';

export interface AddOnEntitlementConfig {
  [key: string]: unknown;
}

export interface CompanyAddOnCatalogItem {
  key: AddOnKey | string;
  addon_type?: string;
  label: string;
  description?: string;
  assignment_modes?: AddOnAssignmentMode[];
  effect?: {
    type?: string;
    entitlement_key?: string;
    limit_key?: string;
    override_value?: unknown;
    override_value_unit?: string;
    config?: string[];
  };
}

export interface CompanyAddOnEntitlement {
  key: AddOnKey | string;
  label: string;
  enabled: boolean;
  assignment_mode: AddOnAssignmentMode;
  user_ids: number[];
  config: AddOnEntitlementConfig;
  revision?: number;
  assignment_id?: number | null;
  status?: string;
  description?: string;
  assignment_modes?: AddOnAssignmentMode[];
  effective_enabled?: boolean;
  effective_from?: string | null;
  effective_until?: string | null;
  expired?: boolean;
}

export interface EffectiveAddOnEntitlement {
  enabled: boolean;
  source: AddOnApplicabilitySource;
  editable: boolean;
  config: AddOnEntitlementConfig;
}

export type EffectiveAddOnEntitlements = Record<string, EffectiveAddOnEntitlement>;

export interface CompanyAddOnEntitlementsResponse {
  company_id: number;
  available_addons?: CompanyAddOnCatalogItem[];
  assigned_addons?: CompanyAddOnEntitlement[];
  addons_by_key?: Record<string, CompanyAddOnEntitlement>;
  enabled_addon_keys?: string[];
  effective_company_entitlements?: string[];
  limits?: AddOnEntitlementConfig;
  addons?: CompanyAddOnEntitlement[];
}

export interface UserEffectiveEntitlementsResponse {
  user_id: number;
  company_id: number;
  addons: EffectiveAddOnEntitlements;
}

export interface SettingsValueMap {
  [key: string]: unknown;
}

export interface SettingsFieldEntitlement {
  field: string;
  addon_key: AddOnKey | string;
  enabled: boolean;
  editable: boolean;
  source: AddOnApplicabilitySource;
  config: AddOnEntitlementConfig;
  reason?: string;
}

export interface SettingsFieldEntitlementMap {
  [field: string]: SettingsFieldEntitlement;
}

export interface EmployeeSettingsEntitlementRow {
  user_id: number;
  name: string;
  email: string;
  personalized_settings: boolean;
  settings: SettingsValueMap;
  addons: EffectiveAddOnEntitlements;
  field_entitlements?: SettingsFieldEntitlementMap;
}

export interface CompanySettingsEntitlementsResponse {
  company_id: number;
  company_settings: SettingsValueMap;
  company_addons: CompanyAddOnEntitlement[];
  all_users_addons?: CompanyAddOnEntitlement[];
  specific_users_addons?: CompanyAddOnEntitlement[];
  addon_limits?: AddOnEntitlementConfig;
  company_field_entitlements?: SettingsFieldEntitlementMap;
  employees: EmployeeSettingsEntitlementRow[];
}

export interface CompanyAddOnAssignmentPayload {
  company_id: number;
  addon_key: AddOnKey | string;
  enabled: boolean;
  assignment_mode?: AddOnAssignmentMode;
  status?: 'active' | 'suspended' | 'cancelled' | 'expired';
  expected_revision?: number | null;
  user_ids?: number[];
  config?: AddOnEntitlementConfig;
  expires_at?: string | null;
  limit_minutes?: number;
  unlimited?: boolean;
}

export interface AddOnControlledSetting {
  field: string;
  addonKey: AddOnKey;
  label: string;
  scopes: SettingsScope[];
}

export const ADD_ON_CONTROLLED_SETTINGS: AddOnControlledSetting[] = [
  {
    field: 'ss_webcam',
    addonKey: 'webcam_capture',
    label: 'Webcam photo capture',
    scopes: ['company', 'employee'],
  },
  {
    field: 'ss_screen_capture_time',
    addonKey: 'custom_periodic_interval',
    label: 'Custom periodic screenshot interval',
    scopes: ['company', 'employee'],
  },
];
